import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Volume2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Shuffle,
  Star,
  RotateCcw,
  Heart
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Vocabulary categories for display
const VOCAB_CATEGORIES = {
  general: 'General',
  greetings: 'Greetings',
  commands: 'Commands',
  counting: 'Counting',
  techniques: 'Techniques',
  body_parts: 'Body Parts',
  positions: 'Positions/Stances',
  etiquette: 'Etiquette',
  weapons: 'Weapons',
  directions: 'Directions',
  equipment: 'Equipment',
};

/**
 * Flip Card Component for review mode
 */
const ReviewFlipCard = ({ term, isFlipped, onFlip, onPlayAudio, showKanji, isFavorite, onToggleFavorite, togglingFavorite }) => {
  return (
    <div
      className="relative w-full max-w-md h-72 cursor-pointer mx-auto"
      onClick={onFlip}
    >
      <div
        className={`relative w-full h-full transition-transform duration-500`}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front of card - Question */}
        <div
          className="absolute w-full h-full rounded-xl shadow-lg bg-gradient-to-br from-yellow-500 to-yellow-600 text-white flex flex-col items-center justify-center p-6"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {isFavorite && (
              <Heart className="h-5 w-5 text-pink-300 fill-current" />
            )}
            <Star className="h-5 w-5 text-yellow-200" />
          </div>
          <p className="text-sm text-yellow-100 mb-2">Mastered Word</p>
          {showKanji && term.japanese && (
            <p className="text-4xl font-bold mb-2">{term.japanese}</p>
          )}
          <p className="text-3xl font-bold mb-4">{term.romaji}</p>
          {term.audio_url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onPlayAudio();
              }}
              className="text-yellow-100 hover:bg-yellow-400 mt-2"
            >
              <Volume2 className="h-5 w-5 mr-2" />
              Listen
            </Button>
          )}
          <p className="text-yellow-200 text-sm mt-4">Tap to reveal meaning</p>
        </div>

        {/* Back of card - Answer */}
        <div
          className="absolute w-full h-full rounded-xl shadow-lg bg-white dark:bg-gray-800 border-2 border-yellow-300 dark:border-yellow-600 flex flex-col items-center justify-center p-6"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {/* Favorite button in top right */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            disabled={togglingFavorite}
            className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
              isFavorite
                ? 'text-pink-500 hover:text-pink-600'
                : 'text-gray-400 dark:text-gray-500 hover:text-pink-500'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart className={`h-6 w-6 ${isFavorite ? 'fill-current' : ''}`} />
          </button>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">English Meaning</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 text-center mb-4">{term.english}</p>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 w-full text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Japanese</p>
            <p className="text-xl text-yellow-700 dark:text-yellow-500">{term.romaji}</p>
            {showKanji && term.japanese && (
              <p className="text-lg text-gray-600 dark:text-gray-400">{term.japanese}</p>
            )}
          </div>

          {term.audio_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onPlayAudio();
              }}
              className="mt-4"
            >
              <Volume2 className="h-4 w-4 mr-2" />
              Listen Again
            </Button>
          )}

          <div className="flex items-center gap-1 mt-4 text-yellow-600">
            <Star className="h-4 w-4" />
            <span className="text-sm">Mastered ({term.correct_count || 3}+ correct)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Review mode for mastered vocabulary words using flip cards.
 */
const VocabReviewMode = ({
  studentId,
  token,
  showKanji = false,
  category = '',
  onCategoryChange
}) => {
  const [terms, setTerms] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Favorites tracking
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  const audioRef = useRef(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Toggle favorite for a term
  const toggleFavorite = async (termId) => {
    if (togglingFavorite) return;
    setTogglingFavorite(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/${termId}/favorite`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          if (data.is_favorite) {
            newSet.add(termId);
          } else {
            newSet.delete(termId);
            // If showing favorites only and we unfavorited, remove from terms
            if (showFavoritesOnly) {
              setTerms(prevTerms => {
                const filtered = prevTerms.filter(t => t.id !== termId);
                // Adjust currentIndex if needed
                if (currentIndex >= filtered.length && filtered.length > 0) {
                  setCurrentIndex(filtered.length - 1);
                }
                return filtered;
              });
            }
          }
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setTogglingFavorite(false);
    }
  };

  // Fetch mastered vocabulary (or favorites only)
  const fetchMasteredTerms = useCallback(async () => {
    setLoading(true);
    try {
      let url = showFavoritesOnly
        ? `${API_BASE}/api/customer/students/${studentId}/vocabulary/favorites`
        : `${API_BASE}/api/customer/students/${studentId}/vocabulary/mastered`;
      if (category) {
        url += `?category=${category}`;
      }

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // Shuffle for variety
        const shuffled = [...data].sort(() => Math.random() - 0.5);
        setTerms(shuffled);
        setCurrentIndex(0);
        setIsFlipped(false);

        // Update favorite IDs set
        const favIds = new Set(data.filter(t => t.is_favorite).map(t => t.id));
        setFavoriteIds(favIds);
      }
    } catch (error) {
      console.error('Failed to fetch vocabulary:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId, category, showFavoritesOnly, getAuthHeaders]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/vocabulary/categories`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [studentId, getAuthHeaders]);

  useEffect(() => {
    fetchMasteredTerms();
    fetchCategories();
  }, [fetchMasteredTerms, fetchCategories]);

  const currentTerm = terms[currentIndex];

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const playAudio = () => {
    if (audioRef.current && currentTerm?.audio_url) {
      audioRef.current.src = currentTerm.audio_url;
      audioRef.current.load();
      audioRef.current.play();
    }
  };

  const goToNext = () => {
    setIsFlipped(false);
    if (currentIndex < terms.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const goToPrevious = () => {
    setIsFlipped(false);
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const shuffleCards = () => {
    const shuffled = [...terms].sort(() => Math.random() - 0.5);
    setTerms(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleCategorySelect = (newCategory) => {
    if (onCategoryChange) {
      onCategoryChange(newCategory);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-600" />
      </div>
    );
  }

  if (terms.length === 0) {
    return (
      <div className="space-y-4">
        {/* Show filters even when empty */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={category}
              onChange={(e) => handleCategorySelect(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {VOCAB_CATEGORIES[cat] || cat}
                </option>
              ))}
            </select>

            {/* Favorites filter toggle */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-800">
              <Heart className={`h-4 w-4 ${showFavoritesOnly ? 'text-pink-500 fill-current' : 'text-pink-400'}`} />
              <label className="text-sm text-pink-700 dark:text-pink-300">Favorites</label>
              <Switch
                checked={showFavoritesOnly}
                onCheckedChange={setShowFavoritesOnly}
              />
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            {showFavoritesOnly ? (
              <>
                <Heart className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No favorite words yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {category
                    ? 'No favorites in this category.'
                    : 'Tap the heart on mastered words to add them to your favorites.'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Favorites help you quickly review words you want to focus on.
                </p>
              </>
            ) : (
              <>
                <Star className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No mastered words yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {category
                    ? 'No mastered words in this category yet.'
                    : 'Complete the quiz to master words and they will appear here for review.'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Get 3 correct answers on a word to master it!
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={category}
            onChange={(e) => handleCategorySelect(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {VOCAB_CATEGORIES[cat] || cat}
              </option>
            ))}
          </select>

          <Button variant="outline" size="sm" onClick={shuffleCards}>
            <Shuffle className="h-4 w-4 mr-2" />
            Shuffle
          </Button>

          {/* Favorites filter toggle */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-800">
            <Heart className={`h-4 w-4 ${showFavoritesOnly ? 'text-pink-500 fill-current' : 'text-pink-400'}`} />
            <label className="text-sm text-pink-700 dark:text-pink-300">Favorites</label>
            <Switch
              checked={showFavoritesOnly}
              onCheckedChange={setShowFavoritesOnly}
            />
          </div>
        </div>

        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${showFavoritesOnly ? 'bg-pink-50 dark:bg-pink-900/30' : 'bg-yellow-50 dark:bg-yellow-900/30'}`}>
          {showFavoritesOnly ? (
            <>
              <Heart className="h-5 w-5 text-pink-500 fill-current" />
              <span className="font-medium text-pink-700 dark:text-pink-300">
                {terms.length} Favorite{terms.length !== 1 ? 's' : ''}
              </span>
            </>
          ) : (
            <>
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="font-medium text-yellow-700 dark:text-yellow-300">
                {terms.length} Mastered Words
              </span>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-yellow-500 rounded-full h-2 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / terms.length) * 100}%` }}
        />
      </div>
      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
        Card {currentIndex + 1} of {terms.length}
      </p>

      {/* Flip Card */}
      <div className="py-4">
        <ReviewFlipCard
          term={currentTerm}
          isFlipped={isFlipped}
          onFlip={handleFlip}
          onPlayAudio={playAudio}
          showKanji={showKanji}
          isFavorite={favoriteIds.has(currentTerm?.id)}
          onToggleFavorite={() => toggleFavorite(currentTerm?.id)}
          togglingFavorite={togglingFavorite}
        />
      </div>

      {/* Navigation */}
      <div className="flex justify-center gap-4">
        <Button
          variant="outline"
          onClick={goToPrevious}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Previous
        </Button>

        <Button
          variant="outline"
          onClick={handleFlip}
        >
          <RotateCcw className="h-5 w-5 mr-1" />
          Flip
        </Button>

        <Button
          variant="outline"
          onClick={goToNext}
        >
          Next
          <ChevronRight className="h-5 w-5 ml-1" />
        </Button>
      </div>

      {/* Info card */}
      <Card className={`mt-6 ${showFavoritesOnly ? 'bg-pink-50 border-pink-200' : 'bg-yellow-50 border-yellow-200'}`}>
        <CardContent className="p-4">
          {showFavoritesOnly ? (
            <>
              <h3 className="font-medium text-pink-800 mb-2 flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Favorites Mode
              </h3>
              <p className="text-sm text-pink-700">
                These are your favorite mastered words. Tap the heart on the back of any card to add or remove favorites.
              </p>
            </>
          ) : (
            <>
              <h3 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                <Star className="h-5 w-5" />
                Review Mode
              </h3>
              <p className="text-sm text-yellow-700">
                These are words you've mastered by getting 3+ correct answers in quiz mode.
                Review them regularly to keep them fresh in your memory!
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VocabReviewMode;
